/**
 * Ooop it — Design Your Own Customizer
 * Canvas-based logo placement + draggable custom text
 * Cloudinary upload + Shopify line item properties
 */

(function () {
  'use strict';

  const cfg = window.DYOConfig;
  if (!cfg) return;

  // ─── DOM refs ────────────────────────────────────────────────────────────────
  const canvas         = document.getElementById('dyo-canvas');
  const uploadBtn      = document.getElementById('dyo-upload-btn');
  const uploadIcon     = document.getElementById('dyo-upload-icon');
  const uploadLabel    = document.getElementById('dyo-upload-label');
  const fileInput      = document.getElementById('dyo-file-input');
  const confirmArea    = document.getElementById('dyo-confirm-area');
  const confirmBtn     = document.getElementById('dyo-confirm-btn');
  const confirmText    = document.getElementById('dyo-confirm-text');
  const repositionBtn  = document.getElementById('dyo-reposition-btn');
  const submitBtn      = document.getElementById('dyo-submit-btn');
  const submitText     = document.getElementById('dyo-submit-text');
  const submitNote     = document.getElementById('dyo-submit-note');
  const errorMsg       = document.getElementById('dyo-error-msg');
  const propLogoUrl    = document.getElementById('prop-logo-url');
  const propPreviewUrl = document.getElementById('prop-preview-url');
  const propLogoPos    = document.getElementById('prop-logo-position');
  const propTextPos    = document.getElementById('prop-text-position');
  const propCustomText = document.getElementById('prop-custom-text');
  const variantIdInput = document.getElementById('dyo-variant-id');
  const priceAmountEl  = document.getElementById('dyo-price-amount');
  const qtyInput       = document.getElementById('dyo-qty-input');
  const qtyMinus       = document.getElementById('dyo-qty-minus');
  const qtyPlus        = document.getElementById('dyo-qty-plus');
  const form           = document.getElementById('dyo-product-form');
  const textDragHint   = document.getElementById('dyo-text-drag-hint');

  // Custom text refs
  const customTextInput = document.getElementById('dyo-custom-text');
  const customTextCount = document.getElementById('dyo-text-count');
  const textFeeBadge    = document.getElementById('dyo-text-fee-badge');

  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  // ─── Canvas constants ────────────────────────────────────────────────────────
  const CW             = canvas.width;   // 600
  const CH             = canvas.height;  // 600
  const HANDLE_PX      = 9;
  const HANDLE_HIT     = 18;
  const MIN_LOGO_PX    = 24;
  const TEXT_FEE_CENTS = 500;
  const TEXT_PAD_X     = 10;
  const TEXT_PAD_Y     = 6;

  // Logo print zone in canvas pixels
  const PZ = {
    x: Math.round(cfg.printZone.xPct * CW),
    y: Math.round(cfg.printZone.yPct * CH),
    w: Math.round(cfg.printZone.wPct * CW),
    h: Math.round(cfg.printZone.hPct * CH),
  };

  // Text print zone in canvas pixels (separate from logo zone)
  const TPZ = cfg.textPrintZone ? {
    x: Math.round(cfg.textPrintZone.xPct * CW),
    y: Math.round(cfg.textPrintZone.yPct * CH),
    w: Math.round(cfg.textPrintZone.wPct * CW),
    h: Math.round(cfg.textPrintZone.hPct * CH),
  } : {
    // Fallback: strip in lower quarter of the logo zone
    x: PZ.x,
    y: PZ.y + Math.round(PZ.h * 0.72),
    w: PZ.w,
    h: Math.round(PZ.h * 0.25),
  };

  // ─── State ───────────────────────────────────────────────────────────────────
  let productImg           = null;
  let currentProductImgSrc = null;
  let logoImg              = null;
  let logoUrl              = null;
  let previewUploading     = false;

  // Logo object
  let logo = { x: PZ.x, y: PZ.y, w: 0, h: 0, ar: 1 };

  // Logo interaction state
  let ui = {
    selected:   false,
    dragging:   false,
    resizing:   false,
    handle:     null,
    dragOrigin: { mx: 0, my: 0, lx: 0, ly: 0, lw: 0, lh: 0 },
  };

  // Text object — centerX/Y is the stable anchor; w/h measured on each render
  let textObj = {
    centerX:  0,
    centerY:  0,
    fontSize: 28,
    w:        0,
    h:        0,
  };

  // Text interaction state
  let textUi = {
    selected:   false,
    dragging:   false,
    resizing:   false,
    handle:     null,
    dragOrigin: { mx: 0, my: 0, centerX: 0, centerY: 0, fontSize: 0 },
  };

  let textPlaced           = false;
  let previewDebounce      = null;
  let textPositionDebounce = null;

  // ─── Custom text helper ───────────────────────────────────────────────────────
  function getCustomText() {
    return customTextInput?.value.trim() || '';
  }

  // ─── Render ──────────────────────────────────────────────────────────────────
  function render() {
    ctx.clearRect(0, 0, CW, CH);

    // 1. Product image
    if (productImg) {
      ctx.drawImage(productImg, 0, 0, CW, CH);
    } else {
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, CW, CH);
    }

    // 2. Dark overlay — cuts out BOTH logo zone and text zone
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, CW, CH);           // outer (filled)
    ctx.rect(PZ.x,  PZ.y,  PZ.w,  PZ.h);   // logo zone (cut out)
    ctx.rect(TPZ.x, TPZ.y, TPZ.w, TPZ.h);  // text zone (cut out)
    ctx.fillStyle = 'rgba(0,0,0,0.42)';
    ctx.fill('evenodd');
    ctx.restore();

    // 3a. Logo zone border (gold dashed)
    ctx.save();
    ctx.beginPath();
    ctx.rect(PZ.x, PZ.y, PZ.w, PZ.h);
    ctx.strokeStyle = 'rgba(201,168,76,0.85)';
    ctx.lineWidth   = 1.5;
    ctx.setLineDash([7, 5]);
    ctx.stroke();
    ctx.restore();

    // 3b. Text zone border (white dashed — visually distinct from logo zone)
    ctx.save();
    ctx.beginPath();
    ctx.rect(TPZ.x, TPZ.y, TPZ.w, TPZ.h);
    ctx.strokeStyle = 'rgba(237,232,223,0.80)';
    ctx.lineWidth   = 1.5;
    ctx.setLineDash([7, 5]);
    ctx.stroke();
    ctx.restore();

    const customText = getCustomText();

    // 4a. Logo zone placeholder (when no logo uploaded)
    if (!logoImg) {
      ctx.save();
      ctx.font         = '500 11px "Space Mono", monospace';
      ctx.fillStyle    = 'rgba(201,168,76,0.75)';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Your logo here', PZ.x + PZ.w / 2, PZ.y + PZ.h / 2 - 8);
      ctx.font      = '400 10px "Space Mono", monospace';
      ctx.fillStyle = 'rgba(237,232,223,0.40)';
      ctx.fillText('Upload to get started', PZ.x + PZ.w / 2, PZ.y + PZ.h / 2 + 10);
      ctx.restore();
    }

    // 4b. Text zone placeholder (when no text entered)
    if (!customText) {
      ctx.save();
      ctx.font         = '500 11px "Space Mono", monospace';
      ctx.fillStyle    = 'rgba(237,232,223,0.70)';
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Your text here', TPZ.x + TPZ.w / 2, TPZ.y + TPZ.h / 2 - 7);
      ctx.font      = '400 10px "Space Mono", monospace';
      ctx.fillStyle = 'rgba(237,232,223,0.35)';
      ctx.fillText('Type below to get started', TPZ.x + TPZ.w / 2, TPZ.y + TPZ.h / 2 + 9);
      ctx.restore();
    }

    // 5. Logo (if present)
    if (logoImg) {
      ctx.drawImage(logoImg, logo.x, logo.y, logo.w, logo.h);
      if (ui.selected) drawLogoHandles();
    }

    // 6. Custom text (draggable, drawn on top of logo)
    if (customText) renderText(customText);
  }

  // ─── Logo handles ─────────────────────────────────────────────────────────────
  function drawLogoHandles() {
    const handles = getLogoHandles();
    ctx.save();
    ctx.strokeStyle = '#C9A84C';
    ctx.lineWidth   = 1;
    ctx.setLineDash([]);
    ctx.strokeRect(logo.x, logo.y, logo.w, logo.h);
    ctx.fillStyle   = '#FFFFFF';
    ctx.strokeStyle = '#C9A84C';
    ctx.lineWidth   = 1.5;
    Object.values(handles).forEach(({ hx, hy }) => {
      const hs = HANDLE_PX;
      ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
      ctx.strokeRect(hx - hs / 2, hy - hs / 2, hs, hs);
    });
    ctx.restore();
  }

  function getLogoHandles() {
    return {
      nw: { hx: logo.x,          hy: logo.y },
      ne: { hx: logo.x + logo.w, hy: logo.y },
      sw: { hx: logo.x,          hy: logo.y + logo.h },
      se: { hx: logo.x + logo.w, hy: logo.y + logo.h },
    };
  }

  // ─── Text rendering ───────────────────────────────────────────────────────────
  function renderText(text) {
    ctx.save();
    ctx.font = `600 ${textObj.fontSize}px "Space Mono", monospace`;

    // Measure and store (used by hit testing after this render)
    textObj.w = ctx.measureText(text).width;
    textObj.h = textObj.fontSize * 1.25;

    const x = textObj.centerX - textObj.w / 2;
    const y = textObj.centerY - textObj.h / 2;

    // Text (no background — rendered as-is over the canvas)
    ctx.fillStyle    = '#ffffff';
    ctx.textAlign    = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(text, x, y);

    // Selection handles
    if (textUi.selected) drawTextHandles(x, y);

    ctx.restore();
  }

  function drawTextHandles(x, y) {
    const px = x - TEXT_PAD_X;
    const py = y - TEXT_PAD_Y;
    const pw = textObj.w + TEXT_PAD_X * 2;
    const ph = textObj.h + TEXT_PAD_Y * 2;

    ctx.strokeStyle = '#C9A84C';
    ctx.lineWidth   = 1;
    ctx.setLineDash([]);
    ctx.strokeRect(px, py, pw, ph);

    ctx.fillStyle   = '#FFFFFF';
    ctx.strokeStyle = '#C9A84C';
    ctx.lineWidth   = 1.5;
    [[px, py], [px + pw, py], [px, py + ph], [px + pw, py + ph]].forEach(([hx, hy]) => {
      const hs = HANDLE_PX;
      ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
      ctx.strokeRect(hx - hs / 2, hy - hs / 2, hs, hs);
    });
  }

  function getTextHandles() {
    const x = textObj.centerX - textObj.w / 2 - TEXT_PAD_X;
    const y = textObj.centerY - textObj.h / 2 - TEXT_PAD_Y;
    const w = textObj.w + TEXT_PAD_X * 2;
    const h = textObj.h + TEXT_PAD_Y * 2;
    return {
      nw: { hx: x,     hy: y },
      ne: { hx: x + w, hy: y },
      sw: { hx: x,     hy: y + h },
      se: { hx: x + w, hy: y + h },
    };
  }

  // ─── Hit testing ──────────────────────────────────────────────────────────────
  function canvasXY(e) {
    const rect   = canvas.getBoundingClientRect();
    const scaleX = CW / rect.width;
    const scaleY = CH / rect.height;
    const src    = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    };
  }

  function hitLogoHandle(cx, cy) {
    if (!ui.selected) return null;
    for (const [name, { hx, hy }] of Object.entries(getLogoHandles())) {
      if (Math.abs(cx - hx) <= HANDLE_HIT / 2 && Math.abs(cy - hy) <= HANDLE_HIT / 2) return name;
    }
    return null;
  }

  function hitLogo(cx, cy) {
    if (!logoImg) return false;
    return cx >= logo.x && cx <= logo.x + logo.w &&
           cy >= logo.y && cy <= logo.y + logo.h;
  }

  function hitTextHandle(cx, cy) {
    if (!textUi.selected || !textObj.w) return null;
    for (const [name, { hx, hy }] of Object.entries(getTextHandles())) {
      if (Math.abs(cx - hx) <= HANDLE_HIT / 2 && Math.abs(cy - hy) <= HANDLE_HIT / 2) return name;
    }
    return null;
  }

  function hitText(cx, cy) {
    if (!getCustomText() || !textObj.w) return false;
    const x = textObj.centerX - textObj.w / 2 - TEXT_PAD_X;
    const y = textObj.centerY - textObj.h / 2 - TEXT_PAD_Y;
    return cx >= x && cx <= x + textObj.w + TEXT_PAD_X * 2 &&
           cy >= y && cy <= y + textObj.h + TEXT_PAD_Y * 2;
  }

  // ─── Clamp helpers ────────────────────────────────────────────────────────────
  function clampLogo() {
    logo.w = Math.max(MIN_LOGO_PX, Math.min(PZ.w, logo.w));
    logo.h = Math.max(MIN_LOGO_PX, Math.min(PZ.h, logo.h));
    logo.x = Math.max(PZ.x, Math.min(PZ.x + PZ.w - logo.w, logo.x));
    logo.y = Math.max(PZ.y, Math.min(PZ.y + PZ.h - logo.h, logo.y));
  }

  function clampText() {
    // Keep font size within sensible bounds
    textObj.fontSize = Math.max(8, Math.min(80, textObj.fontSize));

    // Keep center such that the pill stays inside the text print zone (TPZ)
    // Use current measured dimensions (may be stale by 1 frame, which is fine)
    const halfW = (textObj.w || 0) / 2 + TEXT_PAD_X;
    const halfH = (textObj.h || 0) / 2 + TEXT_PAD_Y;
    textObj.centerX = Math.max(TPZ.x + halfW, Math.min(TPZ.x + TPZ.w - halfW, textObj.centerX));
    textObj.centerY = Math.max(TPZ.y + halfH, Math.min(TPZ.y + TPZ.h - halfH, textObj.centerY));
  }

  // ─── Text initial placement ───────────────────────────────────────────────────
  function placeTextInitial() {
    // Place in centre of text print zone (separate from logo zone)
    textObj.centerX  = TPZ.x + TPZ.w / 2;
    textObj.centerY  = TPZ.y + TPZ.h / 2;
    textObj.fontSize = 28;
    textObj.w        = 0;
    textObj.h        = 0;
    textUi.selected  = true;
    textPlaced       = true;
  }

  // ─── Text resize via corner handles ──────────────────────────────────────────
  // All four corners scale font size; center stays fixed during resize.
  function handleTextResize(dx, dy) {
    const orig = textUi.dragOrigin;
    let delta;
    switch (textUi.handle) {
      case 'se': delta =  (dx + dy) / 4; break;
      case 'nw': delta = -(dx + dy) / 4; break;
      case 'ne': delta =  (dx - dy) / 4; break;
      case 'sw': delta = (-dx + dy) / 4; break;
      default:   delta = 0;
    }
    textObj.fontSize = Math.max(8, Math.min(80, orig.fontSize + delta));
    textObj.centerX  = orig.centerX;
    textObj.centerY  = orig.centerY;
  }

  // ─── Pointer events ───────────────────────────────────────────────────────────
  function onDown(e) {
    const { x, y } = canvasXY(e);
    const customText = getCustomText();

    // ── Text interaction (checked first so handles are reachable over logo) ──────
    if (customText && textObj.w > 0) {
      const tHandle = hitTextHandle(x, y);
      if (tHandle) {
        e.preventDefault();
        textUi.resizing   = true;
        textUi.handle     = tHandle;
        textUi.dragOrigin = { mx: x, my: y, centerX: textObj.centerX, centerY: textObj.centerY, fontSize: textObj.fontSize };
        render();
        return;
      }
      if (hitText(x, y)) {
        e.preventDefault();
        textUi.dragging   = true;
        textUi.selected   = true;
        textUi.dragOrigin = { mx: x, my: y, centerX: textObj.centerX, centerY: textObj.centerY, fontSize: textObj.fontSize };
        ui.selected       = false; // deselect logo
        render();
        return;
      }
    }

    // ── Logo interaction ──────────────────────────────────────────────────────────
    if (logoImg) {
      const lHandle = hitLogoHandle(x, y);
      if (lHandle) {
        e.preventDefault();
        ui.resizing   = true;
        ui.handle     = lHandle;
        ui.dragOrigin = { mx: x, my: y, lx: logo.x, ly: logo.y, lw: logo.w, lh: logo.h };
        return;
      }
      if (hitLogo(x, y)) {
        e.preventDefault();
        ui.dragging     = true;
        ui.selected     = true;
        textUi.selected = false; // deselect text
        ui.dragOrigin   = { mx: x, my: y, lx: logo.x, ly: logo.y };
        render();
        return;
      }
    }

    // ── Clicked empty area — deselect both ───────────────────────────────────────
    if (logoImg || customText) {
      ui.selected     = false;
      textUi.selected = false;
      render();
    }
  }

  function onMove(e) {
    const isActive = ui.dragging || ui.resizing || textUi.dragging || textUi.resizing;

    if (!isActive) {
      // Cursor feedback only
      const { x, y } = canvasXY(e);
      const cursors   = { nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize' };
      const tHandle   = hitTextHandle(x, y);
      const lHandle   = hitLogoHandle(x, y);
      canvas.style.cursor =
        tHandle       ? cursors[tHandle] :
        lHandle       ? cursors[lHandle] :
        hitText(x, y) ? 'move' :
        hitLogo(x, y) ? 'move' :
        'default';
      return;
    }

    e.preventDefault();
    const { x, y } = canvasXY(e);

    if (ui.dragging) {
      const dx = x - ui.dragOrigin.mx;
      const dy = y - ui.dragOrigin.my;
      logo.x = ui.dragOrigin.lx + dx;
      logo.y = ui.dragOrigin.ly + dy;
      clampLogo();
      schedulePositionUpdate();

    } else if (ui.resizing) {
      const dx   = x - ui.dragOrigin.mx;
      const dy   = y - ui.dragOrigin.my;
      const orig = ui.dragOrigin;
      const ar   = logo.ar;
      switch (ui.handle) {
        case 'se': { logo.w = Math.max(MIN_LOGO_PX, orig.lw + dx); logo.h = logo.w / ar; break; }
        case 'sw': { const nw = Math.max(MIN_LOGO_PX, orig.lw - dx); logo.x = orig.lx + (orig.lw - nw); logo.w = nw; logo.h = nw / ar; break; }
        case 'ne': { const nw = Math.max(MIN_LOGO_PX, orig.lw + dx); logo.y = orig.ly + (orig.lh - nw / ar); logo.w = nw; logo.h = nw / ar; break; }
        case 'nw': { const nw = Math.max(MIN_LOGO_PX, orig.lw - dx); logo.x = orig.lx + (orig.lw - nw); logo.y = orig.ly + (orig.lh - nw / ar); logo.w = nw; logo.h = nw / ar; break; }
      }
      clampLogo();
      schedulePositionUpdate();

    } else if (textUi.dragging) {
      const dx = x - textUi.dragOrigin.mx;
      const dy = y - textUi.dragOrigin.my;
      textObj.centerX = textUi.dragOrigin.centerX + dx;
      textObj.centerY = textUi.dragOrigin.centerY + dy;
      clampText();
      scheduleTextPositionUpdate();

    } else if (textUi.resizing) {
      const dx = x - textUi.dragOrigin.mx;
      const dy = y - textUi.dragOrigin.my;
      handleTextResize(dx, dy);
      clampText();
      scheduleTextPositionUpdate();
    }

    render();
  }

  function onUp() {
    ui.dragging      = false;
    ui.resizing      = false;
    ui.handle        = null;
    textUi.dragging  = false;
    textUi.resizing  = false;
    textUi.handle    = null;
    schedulePositionUpdate();
    if (getCustomText()) scheduleTextPositionUpdate();
  }

  // ─── Position properties (debounced) ─────────────────────────────────────────
  function schedulePositionUpdate() {
    clearTimeout(previewDebounce);
    previewDebounce = setTimeout(() => {
      storeLogoPositionProperty();
      if (logoUrl || getCustomText()) schedulePreviewUpload();
    }, 600);
  }

  function storeLogoPositionProperty() {
    if (!logoImg) return;
    const relX = ((logo.x - PZ.x) / PZ.w * 100).toFixed(1);
    const relY = ((logo.y - PZ.y) / PZ.h * 100).toFixed(1);
    const relW = (logo.w / PZ.w * 100).toFixed(1);
    const relH = (logo.h / PZ.h * 100).toFixed(1);
    if (propLogoPos) propLogoPos.value = `x:${relX}%,y:${relY}%,w:${relW}%,h:${relH}%`;
  }

  function scheduleTextPositionUpdate() {
    clearTimeout(textPositionDebounce);
    textPositionDebounce = setTimeout(() => {
      storeTextPositionProperty();
      schedulePreviewUpload();
    }, 300);
  }

  function storeTextPositionProperty() {
    const text = getCustomText();
    if (!text) { if (propTextPos) propTextPos.value = ''; return; }
    // Store position relative to the TEXT print zone (TPZ), not the logo zone
    const relX    = ((textObj.centerX - TPZ.x) / TPZ.w * 100).toFixed(1);
    const relY    = ((textObj.centerY - TPZ.y) / TPZ.h * 100).toFixed(1);
    const relSize = (textObj.fontSize           / TPZ.h * 100).toFixed(1);
    if (propTextPos) propTextPos.value = `cx:${relX}%,cy:${relY}%,size:${relSize}%`;
  }

  // ─── File handling ────────────────────────────────────────────────────────────
  function onFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    const ALLOWED = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!ALLOWED.includes(file.type)) { showError('Please upload a PNG, JPG, SVG, or WebP image.'); return; }
    if (file.size > 10 * 1024 * 1024) { showError('File must be under 10 MB.'); return; }

    hideError();

    const reader  = new FileReader();
    reader.onload = ev => {
      const img  = new Image();
      img.onload = () => {
        logoImg     = img;
        logo.ar     = img.width / img.height;
        placeLogoInitial();
        ui.selected = true;
        render();
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);

    uploadToCloudinary(file);
  }

  function placeLogoInitial() {
    const targetW = PZ.w * 0.55;
    const targetH = targetW / logo.ar;
    const finalH  = Math.min(targetH, PZ.h * 0.55);
    const finalW  = finalH * logo.ar;
    logo.w = finalW;
    logo.h = finalH;
    logo.x = PZ.x + (PZ.w - finalW) / 2;
    logo.y = PZ.y + (PZ.h - finalH) / 2;
  }

  // ─── Cloudinary upload ────────────────────────────────────────────────────────
  async function uploadToCloudinary(file) {
    if (!cfg.cloudName || !cfg.uploadPreset) {
      showError('Upload service not configured. Please contact the store.');
      return;
    }
    setUploadUI('uploading');
    try {
      const fd = new FormData();
      fd.append('file',          file);
      fd.append('upload_preset', cfg.uploadPreset);
      fd.append('folder',        'ooop-it/logos');
      fd.append('tags',          'customer-logo,dyo');

      const res = await fetch(`https://api.cloudinary.com/v1_1/${cfg.cloudName}/image/upload`, { method: 'POST', body: fd });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error?.message || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      logoUrl    = data.secure_url;
      if (propLogoUrl) propLogoUrl.value = logoUrl;

      setUploadUI('done');
      showConfirmStep();
      storeLogoPositionProperty();
      schedulePreviewUpload();
    } catch (err) {
      console.error('[DYO] Upload error:', err);
      setUploadUI('error');
      showError('Logo upload failed. Please check your connection and try again.');
    }
  }

  let previewUploadTimeout = null;
  function schedulePreviewUpload() {
    clearTimeout(previewUploadTimeout);
    previewUploadTimeout = setTimeout(doPreviewUpload, 900);
  }

  async function doPreviewUpload() {
    if ((!logoUrl && !getCustomText()) || previewUploading) return;
    previewUploading = true;
    try {
      const blob = await canvasToBlob(canvas, 'image/jpeg', 0.88);
      const fd   = new FormData();
      fd.append('file',          blob, 'preview.jpg');
      fd.append('upload_preset', cfg.uploadPreset);
      fd.append('folder',        'ooop-it/previews');
      fd.append('tags',          'customer-preview,dyo');

      const res = await fetch(`https://api.cloudinary.com/v1_1/${cfg.cloudName}/image/upload`, { method: 'POST', body: fd });
      if (res.ok) { const data = await res.json(); if (propPreviewUrl) propPreviewUrl.value = data.secure_url; }
    } catch (err) {
      console.warn('[DYO] Preview upload failed (non-critical):', err);
    } finally {
      previewUploading = false;
    }
  }

  function canvasToBlob(cnv, type, quality) {
    return new Promise(resolve => cnv.toBlob(resolve, type, quality));
  }

  // ─── Upload UI states ─────────────────────────────────────────────────────────
  const ICONS = {
    idle:    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>`,
    spinner: `<span class="dyo-spinner" aria-hidden="true"></span>`,
    check:   `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`,
    error:   `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
  };

  function setUploadUI(state) {
    uploadBtn.disabled      = state === 'uploading';
    uploadBtn.dataset.state = state;
    if (uploadIcon)  uploadIcon.innerHTML   = ICONS[state === 'uploading' ? 'spinner' : state === 'done' ? 'check' : state === 'error' ? 'error' : 'idle'];
    if (uploadLabel) uploadLabel.textContent = { idle: 'Upload Your Logo', uploading: 'Uploading…', done: 'Change Logo', error: 'Try Again' }[state] || 'Upload Your Logo';
  }

  function showConfirmStep() {
    if (confirmArea)   confirmArea.hidden        = false;
    if (repositionBtn) repositionBtn.hidden       = true;
    if (confirmBtn)  { confirmBtn.hidden = false; confirmBtn.dataset.state = ''; }
    if (confirmText)   confirmText.textContent   = 'Confirm Placement';
    disableSubmit('Confirm your logo placement to continue');
  }

  function enableSubmit() {
    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-disabled');
    submitText.textContent = 'Add to Cart';
    if (submitNote) submitNote.hidden = true;
  }

  function disableSubmit(message) {
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-disabled', 'true');
    if (submitNote && message) { submitNote.textContent = message; submitNote.hidden = false; }
  }

  function onConfirm() {
    if (!logoUrl) return;
    storeLogoPositionProperty();
    schedulePreviewUpload();
    if (confirmBtn) { confirmBtn.dataset.state = 'confirmed'; confirmBtn.hidden = true; }
    if (repositionBtn) repositionBtn.hidden = false;
    enableSubmit();
  }

  function onReposition() {
    disableSubmit('Confirm your logo placement to continue');
    if (confirmBtn)  { confirmBtn.hidden = false; confirmBtn.dataset.state = ''; }
    if (confirmText)   confirmText.textContent = 'Confirm Placement';
    if (repositionBtn) repositionBtn.hidden    = true;
  }

  function showError(msg) {
    if (!errorMsg) return;
    errorMsg.textContent = msg;
    errorMsg.hidden      = false;
    setTimeout(() => { errorMsg.hidden = true; }, 6000);
  }
  function hideError() { if (errorMsg) errorMsg.hidden = true; }

  // ─── Price with custom text fee ───────────────────────────────────────────────
  function updatePriceWithFee() {
    if (!priceAmountEl) return;
    const variantId = variantIdInput?.value;
    const variant   = cfg.variants?.find(v => String(v.id) === String(variantId));
    if (!variant) return;
    const hasFee = getCustomText().length > 0;
    const total  = variant.price + (hasFee ? TEXT_FEE_CENTS : 0);
    priceAmountEl.textContent = formatMoney(total, cfg.money_format);
    if (textFeeBadge) textFeeBadge.textContent = hasFee ? '+ $5.00' : '';
  }

  // ─── Form submit ──────────────────────────────────────────────────────────────
  async function onSubmit(e) {
    e.preventDefault();

    const customText = getCustomText();
    if (!logoUrl && !customText) {
      showError('Please upload your logo or add custom text before adding to cart.');
      return;
    }

    hideError();
    submitBtn.disabled     = true;
    submitText.textContent = 'Adding to cart…';

    storeLogoPositionProperty();
    storeTextPositionProperty();
    await doPreviewUpload();

    const variantId = variantIdInput ? variantIdInput.value : null;
    const qty       = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    if (propCustomText) propCustomText.value = customText;

    const properties = {
      '_logo_url':      propLogoUrl?.value    || '',
      '_preview_url':   propPreviewUrl?.value || '',
      '_logo_position': propLogoPos?.value    || '',
      'Customized':     'Yes',
    };
    if (customText) {
      properties['Custom Text']    = customText;
      properties['_text_position'] = propTextPos?.value || '';
    }

    const feeVariantId = cfg.customTextFeeVariantId ? parseInt(cfg.customTextFeeVariantId, 10) : null;

    let payload;
    if (customText && feeVariantId) {
      payload = {
        items: [
          { id: variantId, quantity: qty, properties },
          { id: feeVariantId, quantity: 1, properties: { '_note': 'Custom text fee for DYO order' } },
        ],
        sections:     'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      };
    } else {
      payload = {
        id: variantId, quantity: qty, properties,
        sections:     'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      };
    }

    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart.');
      }
      const cartData = await res.json();
      await onCartAddSuccess(cartData);
    } catch (err) {
      console.error('[DYO] Cart add error:', err);
      showError(err.message || 'Something went wrong. Please try again.');
      submitBtn.disabled     = false;
      submitText.textContent = 'Add to Cart';
    }
  }

  let pendingCartSections    = null;
  let cartNeedsDrawerRefresh = false;

  function onCartAddSuccess(cartData) {
    submitText.textContent = 'Added!';
    const cd = document.querySelector('cart-drawer');
    if (cd && cartData?.sections) {
      const iconEl = document.getElementById('cart-icon-bubble');
      if (iconEl && cartData.sections['cart-icon-bubble']) {
        try { iconEl.innerHTML = cd.getSectionInnerHTML(cartData.sections['cart-icon-bubble']); } catch (e) {}
      }
      pendingCartSections    = cartData.sections;
      cartNeedsDrawerRefresh = true;
    }
    setTimeout(() => { submitBtn.disabled = false; submitText.textContent = 'Add to Cart'; }, 2500);
  }

  // ─── Cart icon click — drawer refresh ────────────────────────────────────────
  function setupCartIconRefresh() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;
    cartIcon.addEventListener('click', function (e) {
      if (!cartNeedsDrawerRefresh) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cartNeedsDrawerRefresh = false;

      const cd = document.querySelector('cart-drawer');
      if (!cd) { window.location.href = '/cart'; return; }

      const openDrawer = (sections) => {
        const html = sections?.['cart-drawer'];
        if (html) {
          try {
            const liveDiv  = document.querySelector('#CartDrawer');
            const parsed   = new DOMParser().parseFromString(html, 'text/html');
            const freshDiv = parsed.querySelector('#CartDrawer');
            if (liveDiv && freshDiv) {
              liveDiv.innerHTML = freshDiv.innerHTML;
              const overlay = liveDiv.querySelector('#CartDrawer-Overlay, .cart-drawer__overlay');
              if (overlay) overlay.addEventListener('click', cd.close.bind(cd));
            }
          } catch (err) { /* non-fatal */ }
        }
        cd.classList.remove('is-empty');
        cd.open(cartIcon);
      };

      if (pendingCartSections) {
        const secs = pendingCartSections; pendingCartSections = null; openDrawer(secs);
      } else {
        fetch('/cart?sections=cart-drawer&_=' + Date.now())
          .then(r => r.json()).then(openDrawer)
          .catch(() => { cd.classList.remove('is-empty'); cd.open(cartIcon); });
      }
    }, true);
  }

  // ─── Variant picker ───────────────────────────────────────────────────────────
  function setupVariants() {
    document.querySelectorAll('.dyo-option-radio').forEach(radio => {
      radio.addEventListener('change', resolveVariant);
    });
  }

  function resolveVariant() {
    const selectedOpts = [];
    document.querySelectorAll('.dyo-option-group').forEach(group => {
      const checked = group.querySelector('.dyo-option-radio:checked');
      if (checked) selectedOpts.push(checked.value);
    });
    if (!cfg.variants?.length) return;
    const match = cfg.variants.find(v => v.options.every((opt, i) => opt === selectedOpts[i]));
    if (!match) return;
    if (variantIdInput) variantIdInput.value = match.id;
    loadProductImageUrl(match.featured_image?.src || cfg.productImage);
    updatePriceWithFee();
  }

  function formatMoney(cents, format) {
    if (!format) return `$${(cents / 100).toFixed(2)}`;
    return format.replace('{{amount}}', (cents / 100).toFixed(2))
                 .replace('{{amount_no_decimals}}', Math.round(cents / 100))
                 .replace('{{amount_with_comma_separator}}', (cents / 100).toFixed(2).replace('.', ','));
  }

  // ─── Quantity ─────────────────────────────────────────────────────────────────
  function setupQuantity() {
    qtyMinus?.addEventListener('click', () => { const v = parseInt(qtyInput.value, 10); if (v > 1) qtyInput.value = v - 1; });
    qtyPlus?.addEventListener('click',  () => { qtyInput.value = parseInt(qtyInput.value, 10) + 1; });
  }

  // ─── Product image loader ─────────────────────────────────────────────────────
  function loadProductImageUrl(url) {
    if (!url) { render(); return; }
    if (url === currentProductImgSrc) return;
    currentProductImgSrc = url;
    const img       = new Image();
    img.crossOrigin = 'anonymous';
    img.onload      = () => { productImg = img; render(); };
    img.onerror     = () => render();
    img.src         = url;
  }

  function loadProductImage() { loadProductImageUrl(cfg.productImage); }

  // ─── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    loadProductImage();

    canvas.addEventListener('mousedown',  onDown);
    canvas.addEventListener('mousemove',  onMove);
    window.addEventListener('mousemove',  onMove);
    window.addEventListener('mouseup',    onUp);
    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchmove',  onMove, { passive: false });
    window.addEventListener('touchend',   onUp);

    uploadBtn?.addEventListener('click',  () => fileInput?.click());
    fileInput?.addEventListener('change', onFileSelect);
    confirmBtn?.addEventListener('click',    onConfirm);
    repositionBtn?.addEventListener('click', onReposition);
    form?.addEventListener('submit', onSubmit);

    // Custom text — placement, drag hint, price, canvas
    customTextInput?.addEventListener('input', () => {
      const len = customTextInput.value.length;
      if (customTextCount) customTextCount.textContent = `${len}/30`;

      if (len > 0 && !textPlaced) {
        placeTextInitial();
      } else if (len === 0) {
        textPlaced      = false;
        textUi.selected = false;
      }

      // Show drag hint once text is present
      if (textDragHint) textDragHint.hidden = (len === 0);

      // For text-only orders (no logo uploaded yet), manage submit state
      if (!logoUrl) {
        if (len > 0) {
          enableSubmit();
        } else {
          disableSubmit('Upload your logo or add custom text to continue');
        }
      }

      updatePriceWithFee();
      render();
      if (len > 0) scheduleTextPositionUpdate();
    });

    setupVariants();
    setupQuantity();
    setupCartIconRefresh();
  }

  init();

})();
